import { ref } from 'vue';

// Coupon configuration mapping coupons to subscription plans
const couponConfig = {
    'BF99': {
        planType: 'pro',
        planPeriod: 12, // yearly
        personal: true, // personal plan
        autoOpen: true,
        planUuid: 'pro_yearly',
    },
    'HOLIDAY50': {
        planType: 'pro',
        planPeriod: 12,
        personal: false, // team plan
        autoOpen: true,
        planUuid: 'pro_team_yearly',
    },
    'PRO_ANNUAL': {
        planType: 'pro',
        planPeriod: 12,
        personal: true,
        autoOpen: true,
        planUuid: 'pro_yearly',
    },
    'TEAM_ANNUAL': {
        planType: 'pro',
        planPeriod: 12,
        personal: false,
        autoOpen: true,
        planUuid: 'pro_team_yearly',
    },
    'PLUGIN_STARTUP': {
        planType: 'plugin',
        planPeriod: 1, // monthly
        personal: true,
        autoOpen: true,
        planUuid: 'plugin_startup_monthly',
    },
    'PLUGIN_BUSINESS': {
        planType: 'plugin',
        planPeriod: 1,
        personal: true,
        autoOpen: true,
        planUuid: 'plugin_business_monthly',
    },
    // Add more coupon mappings here
} as const;

export function useCouponUrls() {
    const detectedCoupon = ref<string | null>(null);
    const shouldAutoOpen = ref(false);
    const targetPlan = ref<{
        planUuid: string;
        planType: 'pro' | 'plugin';
        planPeriod: number;
        personal: boolean;
        couponCode: string;
    } | null>(null);

    const getCouponFromUrl = (): string | null => {
        if (typeof window === 'undefined') return null;
        
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('coupon');
    };

    const getCouponConfig = (couponCode: string) => {
        return couponConfig[couponCode.toUpperCase() as keyof typeof couponConfig];
    };

    const shouldOpenModalForCoupon = (couponCode: string, currentPageType: 'pro' | 'plugin'): boolean => {
        const config = getCouponConfig(couponCode);
        if (!config || !config.autoOpen) return false;
        
        // Only auto-open if we're on the correct page type
        return config.planType === currentPageType;
    };

    const getTargetPlanForCoupon = (couponCode: string) => {
        const config = getCouponConfig(couponCode);
        if (!config) return null;
        
        return {
            planUuid: config.planUuid,
            planType: config.planType as 'pro' | 'plugin',
            planPeriod: config.planPeriod,
            personal: config.personal,
            couponCode: couponCode.toUpperCase(),
        };
    };

    const shouldSkipForSubscription = (couponCode: string, pageType: 'pro' | 'plugin', subscription: unknown): boolean => {
        const config = getCouponConfig(couponCode);
        if (!config || config.planType !== pageType) return false;
        
        // If no subscription exists, don't skip
        if (!subscription || typeof subscription !== 'object') return false;
        
        // Type guard for subscription object
        const sub = subscription as { plan?: { uuid?: string } };
        
        // For pro subscriptions, check if they already have the right type
        if (pageType === 'pro') {
            // Check if user has pro subscription and if it matches the coupon's personal/team type
            const planUuid = sub.plan?.uuid;
            if (!planUuid) return false;
            
            const isPersonalSub = !planUuid.includes('team');
            return config.personal === isPersonalSub;
        }
        
        // For plugin subscriptions, skip if they already have any active plugin subscription
        if (pageType === 'plugin') {
            return !!sub.plan?.uuid; // Any plugin subscription means they shouldn't see the modal
        }
        
        return false;
    };

    const initializeCouponDetection = (pageType: 'pro' | 'plugin', subscription: unknown = null) => {
        const couponFromUrl = getCouponFromUrl();
        
        if (couponFromUrl) {
            detectedCoupon.value = couponFromUrl.toUpperCase();
            
            // Check if we should skip due to existing subscription
            if (shouldSkipForSubscription(couponFromUrl, pageType, subscription)) {
                // Clear the coupon from URL since user already has matching subscription
                clearUrlCoupon();
                return;
            }
            
            // Only auto-open if conditions are met
            if (shouldOpenModalForCoupon(couponFromUrl, pageType)) {
                shouldAutoOpen.value = true;
                targetPlan.value = getTargetPlanForCoupon(couponFromUrl);
            }
        }
    };

    const clearUrlCoupon = () => {
        if (typeof window === 'undefined') return;
        
        const url = new URL(window.location.href);
        url.searchParams.delete('coupon');
        window.history.replaceState({}, '', url.toString());
    };

    return {
        detectedCoupon,
        shouldAutoOpen,
        targetPlan,
        getCouponFromUrl,
        getCouponConfig,
        shouldOpenModalForCoupon,
        getTargetPlanForCoupon,
        initializeCouponDetection,
        clearUrlCoupon,
        couponConfig,
    };
}
